import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

const BURGUNDY = '#8B1A1A'
const GOLD = '#B8860B'
const BLUE = '#4a9eff'

const S = {
  shell: { color: '#e0e0e0', fontFamily: "'DM Sans', sans-serif" },
  sectionLabel: { fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.2em', color: GOLD, fontWeight: 600, marginBottom: '1.2rem' },
  statsRow: { display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' },
  statCard: { background: '#111', border: '1px solid #1e1e1e', borderRadius: '8px', padding: '1.25rem 1.5rem', flex: '1', minWidth: '160px' },
  statValue: { fontSize: '1.8rem', fontWeight: 700, color: GOLD, lineHeight: 1 },
  statLabel: { fontSize: '0.72rem', color: '#bbb', marginTop: '0.35rem', textTransform: 'uppercase', letterSpacing: '0.08em' },
  statNote: { fontSize: '0.72rem', color: '#d0d0d0', marginTop: '0.3rem', fontStyle: 'italic' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' },
  card: { background: '#111', border: '1px solid #1e1e1e', borderRadius: '8px', padding: '1.25rem' },
  cardTitle: { fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: GOLD, fontWeight: 600, marginBottom: '1rem', paddingBottom: '0.6rem', borderBottom: '1px solid #1e1e1e' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
  th: { textAlign: 'left', padding: '0.6rem 0.75rem', borderBottom: `1px solid ${BLUE}`, color: BLUE, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em' },
  td: { padding: '0.6rem 0.75rem', borderBottom: '1px solid #1a1a1a', color: '#ccc' },
  tdGold: { padding: '0.6rem 0.75rem', borderBottom: '1px solid #1a1a1a', color: GOLD, fontWeight: 700 },
  sourceNote: { fontSize: '0.72rem', color: '#bbb', borderTop: '1px solid #1a1a1a', paddingTop: '1rem', marginTop: '0.5rem' },
  infoBox: { background: '#0d0505', border: `1px solid ${BURGUNDY}`, borderLeft: `4px solid ${BURGUNDY}`, borderRadius: '8px', padding: '1.25rem 1.5rem', marginBottom: '1.25rem' },
  infoTitle: { fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: BURGUNDY, fontWeight: 600, marginBottom: '0.6rem' },
  infoText: { fontSize: '0.85rem', color: '#d0d0d0', lineHeight: 1.7 },
  ecosystemGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.25rem' },
  ecoCard: { background: '#0d0d0d', border: '1px solid #1e1e1e', borderRadius: '8px', padding: '1rem' },
  ecoValue: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1, marginBottom: '0.3rem' },
  ecoLabel: { fontSize: '0.72rem', color: '#bbb', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.3rem' },
  ecoNote: { fontSize: '0.72rem', color: '#888', fontStyle: 'italic', lineHeight: 1.4 },
  trendUp: { color: '#10b981' },
  trendDown: { color: BURGUNDY },
  trendFlat: { color: GOLD },
}

const BAY_STATS = [
  { value: '51,000 MT', label: 'Bay Cap', note: 'Annual Chesapeake Bay allocation since 2012 amendment' },
  { value: '34%', label: 'Of Total Quota', note: 'Bay cap as share of coastwide Atlantic quota (~153,000 MT)' },
  { value: '2012', label: 'Last Amended', note: 'ASMFC addendum establishing current Bay cap' },
  { value: '3,500 sq mi', label: 'Bay Watershed', note: 'Chesapeake Bay surface area — primary menhaden nursery habitat' },
]

const ECO_INDICATORS = [
  { value: '↓ 28%', label: 'Striped Bass Index', note: '2018–2023 ASMFC stock assessment trend', trend: 'trendDown' },
  { value: '~Stable', label: 'Menhaden Biomass', note: '2023 coastwide assessment: not overfished, overfishing not occurring', trend: 'trendFlat' },
  { value: '↑ 12%', label: 'Bay Hypoxia Events', note: 'Low-oxygen dead zones, 2015–2023 NOAA monitoring', trend: 'trendDown' },
  { value: '↓ 18%', label: 'Osprey Productivity', note: 'Chesapeake Bay Osprey productivity index 2010–2022', trend: 'trendDown' },
  { value: '↓ 41%', label: 'Atlantic Bluefin Tuna', note: 'ICCAT 2022: juvenile abundance index, NW Atlantic', trend: 'trendDown' },
  { value: '↑ 7%', label: 'Humpback Strandings', note: 'NOAA unusual mortality event 2016–present, mid-Atlantic', trend: 'trendDown' },
]

const STATE_DATA = [
  { state: 'Virginia', quota: 'Primarily reduction', landingsMT: 78400, facilities: 'Reedville (Omega Protein)', notes: 'Dominant reduction state; only Atlantic reduction port' },
  { state: 'New Jersey', quota: 'Bait/food fish', landingsMT: 19200, facilities: 'Multiple small ports', notes: 'Primarily bait fishery; no reduction' },
  { state: 'North Carolina', quota: 'Bait/food fish', landingsMT: 14700, facilities: 'Beaufort, Morehead City', notes: 'Significant bait market, pound net operations' },
  { state: 'Maryland', quota: 'Bait/food fish', landingsMT: 8900, facilities: 'Chesapeake Bay ports', notes: 'Bay-heavy; state moratorium on reduction since 2005' },
  { state: 'Massachusetts', quota: 'Bait/food fish', landingsMT: 7300, facilities: 'Cape Cod, Gloucester', notes: 'Lobster bait primary use' },
  { state: 'Other states', quota: 'Bait/food fish', landingsMT: 4200, facilities: 'Various', notes: 'NY, DE, CT, ME combined' },
]

const chartData = STATE_DATA.map(s => ({ state: s.state.replace(' states', ''), landings: s.landingsMT }))

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  return (
    <div style={{ background: '#181818', border: '1px solid #2a2a2a', borderRadius: '6px', padding: '0.6rem 0.8rem', fontSize: '0.82rem', color: '#ccc' }}>
      <div style={{ color: GOLD, fontWeight: 700, marginBottom: '0.2rem' }}>{label}</div>
      <div>{payload[0].value.toLocaleString()} MT</div>
    </div>
  )
}

export default function RegionalAnalysis() {
  return (
    <div style={S.shell}>
      <div style={S.sectionLabel}>Regional Analysis</div>

      <div style={S.statsRow}>
        {BAY_STATS.map(c => (
          <div key={c.label} style={S.statCard}>
            <div style={S.statValue}>{c.value}</div>
            <div style={S.statLabel}>{c.label}</div>
            <div style={S.statNote}>{c.note}</div>
          </div>
        ))}
      </div>

      <div style={S.infoBox}>
        <div style={S.infoTitle}>Chesapeake Bay — The Nursery</div>
        <div style={S.infoText}>
          The Chesapeake Bay is the most critical menhaden nursery habitat on the Atlantic coast.
          Juvenile menhaden spend their first year in bay waters before migrating coastwide.
          The 51,000 MT Bay cap — set in 2012 and unchanged since — was a concession to ecosystem
          advocates but remains well above what independent scientists recommended. The reduction
          fleet's Reedville, Virginia operations draw directly from this nursery. Maryland imposed
          a unilateral moratorium on reduction fishing in state waters in 2005; Virginia has not.
        </div>
      </div>

      <div style={{ ...S.card, marginBottom: '1.25rem' }}>
        <div style={S.cardTitle}>Chesapeake Bay Ecosystem Indicators</div>
        <div style={S.ecosystemGrid}>
          {ECO_INDICATORS.map(e => (
            <div key={e.label} style={S.ecoCard}>
              <div style={{ ...S.ecoValue, ...S[e.trend] }}>{e.value}</div>
              <div style={S.ecoLabel}>{e.label}</div>
              <div style={S.ecoNote}>{e.note}</div>
            </div>
          ))}
        </div>
        <div style={S.sourceNote}>Sources: ASMFC stock assessments, NOAA CHESRIMS, ICCAT, Chesapeake Bay Program</div>
      </div>

      <div style={S.row}>
        <div style={S.card}>
          <div style={S.cardTitle}>Atlantic Coast Landings by State (2023 est.)</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#1e1e1e" horizontal={false} />
              <XAxis type="number" tick={{ fill: '#666', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="state" tick={{ fill: '#aaa', fontSize: 11 }} axisLine={false} tickLine={false} width={85} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="landings" name="Landings (MT)" fill={BURGUNDY} radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={S.card}>
          <div style={S.cardTitle}>State-by-State Detail</div>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>State</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Landings (MT)</th>
                <th style={S.th}>Use</th>
              </tr>
            </thead>
            <tbody>
              {STATE_DATA.map((row, i) => (
                <tr key={row.state}>
                  <td style={i === 0 ? S.tdGold : S.td}>{row.state}</td>
                  <td style={{ ...S.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{row.landingsMT.toLocaleString()}</td>
                  <td style={S.td}>{row.quota}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={S.sourceNote}>Sources: NOAA Fisheries, ASMFC 2023 data</div>
        </div>
      </div>

      <div style={{ ...S.card, marginBottom: '0' }}>
        <div style={S.cardTitle}>Processing Facilities</div>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>State</th>
              <th style={S.th}>Facilities</th>
              <th style={S.th}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {STATE_DATA.map(row => (
              <tr key={row.state}>
                <td style={S.td}>{row.state}</td>
                <td style={S.td}>{row.facilities}</td>
                <td style={{ ...S.td, color: '#aaa', fontSize: '0.8rem' }}>{row.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
